// Pure logic behind the Form Builder: the canvas field model (plain-English
// vis/req answers instead of raw ui_meta), the label→key derivation, the
// container/row packing helpers, and the widget-tree surgery behind the
// drag gestures. The ui_meta translation MUST stay in lockstep with
// client/src/form-engine/config.ts or the canvas will lie.
import type { FieldDefinition, FieldOption, FormCard, FormConfig, FormLayout, FormRule, FormSection, FormWidget, GradientDir, SectionStyle, WidgetCount, WidgetNode } from '../../shared/types';
import { GRADIENT_DIRS, MAX_CARD_DEPTH } from '../../shared/types';
import { actionText } from '../../form-engine/rules';
import { renameKeyInDocument } from '../../shared/documentRename';

export { MAX_CARD_DEPTH };

/** 'shown' = on the form · 'hidden' = kept in the catalog but not rendered. */
export type Visibility = 'shown' | 'hidden';
/** Requiredness is answered once, here — no separate flags. */
export type Requiredness = 'never' | 'whenShown';
/** Whether the respondent can type into it at all. A rule can overrule this. */
export type Lockedness = 'editable' | 'locked';
export type BInputType =
  | 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'checkbox' | 'textarea' | 'file'
  | 'radio' | 'multiselect' | 'rating' | 'time' | 'url' | 'payment' | 'name' | 'address'
  /** a drawn or typed signature; the answer is the URL of the stored PNG */
  | 'signature'
  /** P3: a linear scale, rows × columns, options in order, a date with a time */
  | 'scale' | 'matrix' | 'ranking' | 'datetime'
  /** presentation only — a rule, a heading, a paragraph or a picture; none asks anything */
  | 'divider' | 'heading' | 'paragraph' | 'image';

/** The types that ask nothing. Twin of PRESENTATION_TYPES in shared/types.ts. */
export const PRESENTATION: ReadonlySet<string> = new Set(['divider', 'heading', 'paragraph', 'image']);

/** A divider's look, edited as strings so the panel inputs can sit empty. */
export interface BDivider { thickness: string; color: string; space: string }
export const emptyDivider = (): BDivider => ({ thickness: '1', color: '', space: '20' });

/** The content blocks (U5), edited as strings so the panel inputs can sit empty. */
export interface BHeading { level: '1' | '2' | '3' }
export const emptyHeading = (): BHeading => ({ level: '1' });
export interface BParagraph { text: string }
export const emptyParagraph = (): BParagraph => ({ text: '' });
export interface BImage { url: string; alt: string; width: string; align: 'left' | 'center' | 'right' }
export const emptyImage = (): BImage => ({ url: '', alt: '', width: '', align: 'center' });

/** ui_meta.signature edited as strings so the panel inputs can sit empty. */
export interface BSignature {
  /** allow drawing with a finger, mouse or stylus */
  draw: boolean;
  /** allow typing a name, rendered in a script face */
  type: boolean;
  penColor: string;
  height: string;
  consentText: string;
  /** The contract language the signature agrees to; {{tokens}} allowed. */
  terms: string;
}
/**
 * Both ways on by default.
 *
 * Draw-only was the first default and it read as a missing feature: the field
 * offers a pad, and somebody who wanted a typed name went looking for the
 * setting rather than finding it already on. Typing is the accessible option
 * of the two — a signature nobody can draw with a trackpad is still a
 * signature — so it is the one that should not have to be switched on.
 */
export const emptySignature = (): BSignature =>
  ({ draw: true, type: true, penColor: '', height: '', consentText: '', terms: '' });

/** ui_meta.payment edited as strings so the panel inputs can sit empty. */
export interface BPayment { amountMode: 'fixed' | 'field'; amountFixed: string; amountField: string; currency: string; description: string }
export const emptyPayment = (): BPayment => ({ amountMode: 'fixed', amountFixed: '', amountField: '', currency: 'usd', description: '' });

/** ui_meta.upload edited as strings so the panel inputs can sit empty (= inherit the form's setting). */
export interface BUpload { maxFiles: string; maxSizeMb: string; accept: string[] }
export const emptyUpload = (): BUpload => ({ maxFiles: '', maxSizeMb: '', accept: [] });

/** Input types whose answers come from an authored option list (a ranking's items are its options). */
export const OPTION_TYPES: BInputType[] = ['select', 'radio', 'multiselect', 'ranking'];
/** The choice types whose options may carry a picture (P3 image choice). */
export const IMAGE_OPTION_TYPES: BInputType[] = ['radio', 'multiselect'];
/**
 * The choice types whose options may carry POINTS.
 *
 * A ranking is left out on purpose: its answer is an order, not a choice, so
 * there is no one option a respondent picked to award the points of.
 */
export const SCORE_TYPES: BInputType[] = ['select', 'radio', 'multiselect'];

/** ui_meta.scale (P3) edited as strings so the panel inputs can sit empty. */
export interface BScale { min: '0' | '1'; max: string; minLabel: string; maxLabel: string }
export const emptyScale = (): BScale => ({ min: '1', max: '5', minLabel: '', maxLabel: '' });
/** The NPS preset: 0–10 with the two words every NPS carries. */
export const npsScale = (): BScale => ({ min: '0', max: '10', minLabel: 'Not at all likely', maxLabel: 'Extremely likely' });
/** ui_meta.matrix (P3): the rows and columns as the panel edits them. */
export interface BMatrix { rows: FieldOption[]; columns: FieldOption[]; multi: boolean }
export const emptyMatrix = (): BMatrix => ({
  rows: [{ value: 'row1', text: 'Row 1' }, { value: 'row2', text: 'Row 2' }],
  columns: [{ value: 'col1', text: 'Column 1' }, { value: 'col2', text: 'Column 2' }, { value: 'col3', text: 'Column 3' }],
  multi: false,
});

/** ui_meta.validation edited as strings so the panel inputs can sit empty. */
export interface BValidation {
  minLen: string; maxLen: string; min: string; max: string; pattern: string; message: string;
  /** 'date' fields: '' · 'YYYY-MM-DD' · 'today' · 'today+10' · 'today-30' */
  dateMin: string; dateMax: string;
}
export const emptyValidation = (): BValidation =>
  ({ minLen: '', maxLen: '', min: '', max: '', pattern: '', message: '', dateMin: '', dateMax: '' });

/** How a date bound is edited: which kind it is, and the number/date with it. */
export type DateBoundKind = '' | 'today' | 'offset' | 'fixed';
export function readDateBound(spec: string): { kind: DateBoundKind; days: string; date: string } {
  const raw = (spec || '').trim();
  if (!raw) return { kind: '', days: '', date: '' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { kind: 'fixed', days: '', date: raw };
  const rel = /^today(?:\s*([+-])\s*(\d{1,5}))?$/i.exec(raw);
  if (!rel) return { kind: '', days: '', date: '' };
  if (!rel[1]) return { kind: 'today', days: '', date: '' };
  return { kind: 'offset', days: `${rel[1] === '-' ? '-' : ''}${rel[2]}`, date: '' };
}
export function writeDateBound(kind: DateBoundKind, days: string, date: string): string {
  if (kind === 'today') return 'today';
  if (kind === 'fixed') return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
  if (kind === 'offset') {
    // An empty box is a bound still being typed, not "today" — Number('') is 0.
    const raw = String(days).trim();
    if (!raw) return '';
    const n = Number(raw);
    if (!Number.isInteger(n)) return '';
    return n < 0 ? `today-${Math.abs(n)}` : `today+${n}`;
  }
  return '';
}

/**
 * A field as the builder canvas edits it.
 *
 * Three entries are not fields at all. A `slot` is an empty half of a Row
 * waiting to be filled. A `cardId` entry is a CARD's place in the canvas, and
 * a `nodeScope` entry is a nested WIDGET's — those elements keep their own
 * settings on the section or in the widget tree, but WHERE they sit (which
 * container, inside which card, and in what order among the loose fields) is
 * one question, and this array is where the canvas answers it for everything.
 * All three are dropped on the way to a published definition.
 */
export interface BField {
  id?: number;
  key: string;
  label: string;
  section: string;
  group: string;
  row: string | null;
  type: BInputType | 'slot' | 'card' | 'wnode';
  scope: string;
  flow: 'both' | 'simple' | 'complex';
  vis: Visibility;
  req: Requiredness;
  lock: Lockedness;
  options: FieldOption[];
  /** built-in option lists: 'us_states' (paired with countryField) or 'countries' */
  optionsPreset: '' | 'us_states' | 'countries';
  countryField: string;
  placeholder: string;
  infoText: string;
  /** 'rating' fields: top of the scale, as typed ('' = default 5) */
  ratingMax?: string;
  /** 'address' fields: country pre-selected on the form ('' = none) */
  addressDefaultCountry?: string;
  /** 'address' fields: '' = standard; 'shipping' adds Company/Name, Attention, Phone */
  addressKind?: '' | 'shipping';
  /** 'divider' fields: how the rule is drawn */
  divider?: BDivider;
  /** 'heading' blocks: the size (the text is the label) */
  heading?: BHeading;
  /** 'paragraph' blocks: the rich text */
  paragraph?: BParagraph;
  /** 'image' blocks: the picture and how it sits */
  image?: BImage;
  /** 'signature' fields: how it may be signed, and how the pad looks */
  signature?: BSignature;
  /** the answer the form opens with ('' / undefined = start blank) */
  defaultValue?: string | string[] | boolean;
  /** answer constraints, edited in the panel's Validation block */
  validation?: BValidation;
  /** 'payment' fields: what the field charges */
  payment?: BPayment;
  /** 'file' fields: limits over the form's own ('' / [] = inherit) */
  upload?: BUpload;
  /** choice fields: offer an "Other" write-in, and what to call it ('' = "Other") */
  allowOther?: boolean;
  otherLabel?: string;
  /** 'tel' fields: format as a US number and keep the digits only */
  phoneUs?: boolean;
  /** 'scale' fields (P3): the run of numbers and its end labels */
  scale?: BScale;
  /** 'matrix' fields (P3): rows, columns, one-or-many per row */
  matrix?: BMatrix;
  /** legacy conditional kept as passthrough — the Logic page owns new conditions */
  showWhen?: { field: string; equals: string; orFlag?: string } | null;
  slot?: boolean;
  /** this entry marks where the card with this id sits (see BField) */
  cardId?: string;
  /** this entry marks where the widget container with this scope sits */
  nodeScope?: string;
  isNew?: boolean;
  keyTouched?: boolean;
}

/** An entry that stands for structure rather than for a question. */
export const isStructural = (f: BField): boolean => !!f.slot || !!f.cardId || !!f.nodeScope;

/**
 * A key only a marker ever owns.
 *
 * Markers are rebuilt from the layout and the widget tree on every load, so a
 * STORED field carrying one of these keys is never the marker itself — it is a
 * marker a past publish let through as if it were a question. Left in place it
 * shadows the real marker (same key, no structure) and deleting the impostor
 * took the container with it, so it is dropped on the way in.
 */
export const isMarkerKey = (key: string): boolean =>
  key.startsWith('__card_') || key.startsWith('__wnode_');
/**
 * A real element that asks nothing — a divider, a heading, a paragraph, a
 * picture. It publishes as a field (that is where position lives) but carries
 * no answer, so it stays out of submissions, rule pickers, table columns and
 * the per-form element count.
 */
export const asksNothing = (f: { type: BField['type'] }): boolean => PRESENTATION.has(f.type);
/** The questions on the canvas: no placeholders, no cards, no presentation. */
export const questionsIn = (fields: BField[]): BField[] =>
  fields.filter((f) => !isStructural(f) && !asksNothing(f));

/** A card's place on the canvas: which container, and inside which card. */
export function mkCardMarker(cardId: string, c: Cont): BField {
  return {
    key: `__card_${cardId}`, cardId, label: '', type: 'card',
    section: c.sec, group: c.grp, scope: c.scope,
    flow: c.scope === 'order' ? 'both' : 'complex', row: null, vis: 'shown', req: 'never', lock: 'editable',
    options: [], optionsPreset: '', countryField: '', showWhen: null, placeholder: '', infoText: '',
  };
}

/** A nested container's place on the canvas: which container it repeats in. */
export function mkNodeMarker(scope: string, c: Cont): BField {
  return {
    key: `__wnode_${scope}`, nodeScope: scope, label: '', type: 'wnode',
    section: c.sec, group: c.grp, scope: c.scope,
    flow: 'complex', row: null, vis: 'shown', req: 'never', lock: 'editable',
    options: [], optionsPreset: '', countryField: '', showWhen: null, placeholder: '', infoText: '',
  };
}

/**
 * An id like `card11_2` that no card on the form answers to yet.
 *
 * `taken` is every card id anywhere on the form, not just this section's, so a
 * repaired card cannot collide with one somewhere else either.
 */
function freeCardId(base: string, taken: Set<string>): string {
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/**
 * Cards that ended up sharing an id, given ids of their own.
 *
 * Fields point at the card that groups them BY id, so two cards answering to
 * one id both claimed the same fields and drew them twice — a dropped card
 * looked like a duplicate of an existing one rather than a new empty card.
 *
 * The card that appears FIRST keeps the id, and with it the fields and any
 * card nested inside it. The later one is the one that was just dropped, so it
 * takes the new id and arrives empty, which is what it was meant to be.
 */
export function splitDuplicateCards(
  secs: BSection[], fields: BField[]
): { sections: BSection[]; fields: BField[] } {
  const taken = new Set<string>();
  for (const s of secs) for (const c of s.cards || []) taken.add(c.id);
  const out = fields.map((f) => ({ ...f }));
  let changed = false;

  const sections = secs.map((s) => {
    // Resolved up front: renaming a marker as we go would hide it from the
    // lookup for the next card sharing that id.
    const markers = new Map<string, BField[]>();
    for (const f of out) {
      if (!f.cardId || f.section !== s.id) continue;
      const list = markers.get(f.cardId);
      if (list) list.push(f); else markers.set(f.cardId, [f]);
    }
    const nth = new Map<string, number>();
    const cards = (s.cards || []).map((c) => {
      const k = nth.get(c.id) ?? 0;
      nth.set(c.id, k + 1);
      if (k === 0) return c;
      const id = freeCardId(c.id, taken);
      taken.add(id);
      changed = true;
      const m = markers.get(c.id)?.[k];
      if (m) { m.cardId = id; m.key = `__card_${id}`; }
      return { ...c, id };
    });
    return { ...s, cards };
  });

  return changed ? { sections, fields: out } : { sections: secs, fields };
}

/** Where a nested container's marker is, if it has one. */
export const nodeMarkerOf = (fields: BField[], scope: string): BField | undefined =>
  fields.find((f) => f.nodeScope === scope);

/** Where a card's marker is, if it has one. */
export const markerOf = (fields: BField[], secId: string, cardId: string): BField | undefined =>
  fields.find((f) => f.cardId === cardId && f.section === secId);

/**
 * How deep a card sits in its container: 0 in the body, 1 inside a card, …
 * A card whose marker is missing counts as sitting in the body.
 */
export function cardDepth(fields: BField[], secId: string, cardId: string): number {
  let depth = 0;
  const seen = new Set<string>();
  let cur = cardId;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const m = markerOf(fields, secId, cur);
    if (!m || !m.group) return depth;
    depth += 1;
    cur = m.group;
  }
  return depth;
}

/** A card id and every card nested inside it. */
export function cardAndDescendants(fields: BField[], secId: string, cardId: string): Set<string> {
  const out = new Set<string>([cardId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of fields) {
      if (f.cardId && f.section === secId && f.group && out.has(f.group) && !out.has(f.cardId)) {
        out.add(f.cardId);
        grew = true;
      }
    }
  }
  return out;
}

export interface BStyle {
  bg: string;
  /** second gradient stop; '' = solid */
  bg2: string;
  gradient: GradientDir;
  radius: number;
  width: NonNullable<SectionStyle['width']>;
  height: string;
}

export interface BCard {
  id: string;
  title: string;
  style: BStyle;
  /** '' = the section body; a widget node scope = the card repeats inside it */
  scope: string;
  /** hidden by default — only a `show` rule naming it puts it on the form */
  hidden?: boolean;
}

export interface BSection extends Omit<FormSection, 'cards' | 'style'> {
  cards: BCard[];
  style: BStyle;
}

export const defaultStyle = (): BStyle => ({ bg: 'transparent', bg2: '', gradient: 'vertical', radius: 0, width: '100%', height: '' });

/** BStyle -> the stored SectionStyle (omitting everything left at its default). */
export function toStyleBody(b: BStyle): SectionStyle | undefined {
  const out: SectionStyle = {};
  if (b.bg && b.bg !== 'transparent') out.bg = b.bg;
  if (out.bg && b.bg2) {
    out.bg2 = b.bg2;
    // Vertical is what a box with no direction has always drawn, and is the
    // same fall-through `sanitizeStyle` applies on the way in.
    out.gradient = GRADIENT_DIRS.includes(b.gradient) ? b.gradient : 'vertical';
  }
  if (b.radius) out.radius = b.radius;
  if (b.width && b.width !== '100%') out.width = b.width;
  if (b.height) out.height = b.height;
  return Object.keys(out).length ? out : undefined;
}

/**
 * BCard -> the stored FormCard (scope 'order' is the default, so it is
 * dropped). `place` is what the card's marker says about where it sits: the
 * container it groups, the card it nests in, and its order among that
 * container's fields, on the same scale as their sort_order.
 */
export function toCardBody(
  c: BCard, sortOrder: number, place: { scope: string; parent: string; order?: number } = { scope: c.scope, parent: '' }
): FormCard {
  const scope = place.scope || c.scope;
  return {
    id: c.id,
    title: c.title,
    sortOrder,
    ...(scope && scope !== 'order' ? { scope } : {}),
    ...(place.parent ? { parent: place.parent } : {}),
    ...(place.order !== undefined ? { order: place.order } : {}),
    ...(toStyleBody(c.style) ? { style: toStyleBody(c.style) } : {}),
    ...(c.hidden ? { hidden: true } : {}),
  };
}

/**
 * The whole canvas as a published definition would see it.
 *
 * Fields and cards share ONE running order — the spine of the fields array —
 * because that is the single question the canvas answers about position, and
 * the renderer merges the two back together by exactly these numbers. Slots
 * and card markers consume a number and are then dropped: a gap in the
 * sequence is harmless, a card landing in the wrong place is not.
 */
export function placeItems(fields: BField[]): {
  fieldOrder: Map<string, number>;
  cards: Map<string, { scope: string; parent: string; order: number }>;
  nodes: Map<string, number>;
} {
  const fieldOrder = new Map<string, number>();
  const cards = new Map<string, { scope: string; parent: string; order: number }>();
  const nodes = new Map<string, number>();
  fields.forEach((f, i) => {
    const order = (i + 1) * 10;
    if (f.cardId) cards.set(`${f.section}:${f.cardId}`, { scope: f.scope, parent: f.group, order });
    else if (f.nodeScope) nodes.set(f.nodeScope, order);
    else if (!f.slot) fieldOrder.set(f.key, order);
  });
  return { fieldOrder, cards, nodes };
}

/**
 * The widget trees as a published definition would see them: every nested
 * container stamped with the place its marker holds on the canvas. A root
 * container IS its section, so it has no place among anything and keeps none.
 */
export function toWidgets(widgets: FormWidget[], fields: BField[]): FormWidget[] {
  const { nodes } = placeItems(fields);
  const stamp = (n: WidgetNode, root: boolean): WidgetNode => {
    const order = nodes.get(n.scope);
    const { order: _drop, ...rest } = n;
    return {
      ...rest,
      ...(!root && order !== undefined ? { order } : {}),
      children: (n.children || []).map((c) => stamp(c, false)),
    };
  };
  return widgets.map((w) => (w.tree ? { ...w, tree: stamp(w.tree, true) } : w));
}

/** The canvas sections as a published layout. */
export function toLayout(sections: BSection[], fields: BField[]): FormLayout {
  const { cards } = placeItems(fields);
  return {
    version: 1,
    sections: sections.map((s, i) => ({
      ...s,
      sortOrder: (i + 1) * 10,
      style: toStyleBody(s.style),
      cards: s.cards.map((c, k) => toCardBody(c, (k + 1) * 10, cards.get(`${s.id}:${c.id}`) ?? { scope: c.scope, parent: '' })),
    })) as FormSection[],
  };
}

export const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * First 5 words of the label camelCased, uniquified against taken keys.
 *
 * `reserved` is for names that are not fields but share their namespace — the
 * automatic count keys, which land in the same flat submission object.
 */
export function deriveKeyIn(label: string, fields: { key: string }[], reserved: Iterable<string> = []): string {
  const words = (label || '').replace(/[^a-zA-Z0-9 ]+/g, ' ').trim().split(/\s+/).filter(Boolean).slice(0, 5);
  const k = words.map((w, i) => (i ? cap(w.toLowerCase()) : w.toLowerCase())).join('') || 'field';
  const taken = new Set([...fields.map((f) => f.key), ...reserved]);
  let k2 = k, i = 2;
  while (taken.has(k2)) k2 = k + i++;
  return k2;
}

// ---- FieldDefinition (ui_meta) <-> BField (vis/req answers) -----------------
export function fromDefinition(f: FieldDefinition): BField {
  const m = f.ui_meta || {};
  // Legacy ui_meta (opt-in show flags) collapses to a simple shown/hidden answer:
  // a field that needed a flag nobody sets any more reads as hidden.
  const vis: Visibility = m.alwaysShow || m.defaultShow || !m.showFlag ? 'shown' : 'hidden';
  // Every legacy "required" shape (always / required-when-shown / opt-in flag)
  // collapses to the single honest answer: required whenever the field shows.
  const req: Requiredness = (m.alwaysRequired || f.required_default || m.requiredWhenShown || m.requireFlag)
    ? 'whenShown' : 'never';
  return {
    id: f.id,
    key: f.key,
    label: f.label,
    section: f.section,
    group: m.group || '',
    row: m.row || null,
    type: f.input_type,
    scope: f.scope,
    flow: f.flow_applicability,
    vis, req,
    lock: m.locked ? 'locked' : 'editable',
    options: (f.options || []).map((o) => ({ ...o })),
    optionsPreset: m.optionsPreset || '',
    countryField: m.countryField || '',
    placeholder: m.placeholder || '',
    infoText: m.infoText || '',
    ratingMax: m.ratingMax ? String(m.ratingMax) : '',
    upload: {
      maxFiles: m.upload?.maxFiles ? String(m.upload.maxFiles) : '',
      maxSizeMb: m.upload?.maxSizeMb ? String(m.upload.maxSizeMb) : '',
      accept: Array.isArray(m.upload?.accept) ? m.upload!.accept!.map(String) : [],
    },
    allowOther: !!m.allowOther,
    otherLabel: m.allowOther?.label || '',
    phoneUs: m.phone?.format === 'us',
    scale: {
      min: m.scale?.min === 0 ? '0' : '1',
      max: m.scale?.max !== undefined ? String(m.scale.max) : '5',
      minLabel: m.scale?.minLabel || '',
      maxLabel: m.scale?.maxLabel || '',
    },
    matrix: {
      rows: (m.matrix?.rows || []).map((o) => ({ value: String(o.value ?? ''), text: String(o.text ?? '') })),
      columns: (m.matrix?.columns || []).map((o) => ({ value: String(o.value ?? ''), text: String(o.text ?? '') })),
      multi: !!m.matrix?.multi,
    },
    addressDefaultCountry: m.address?.defaultCountry || '',
    addressKind: m.address?.kind === 'shipping' ? 'shipping' : '',
    signature: {
      // A stored field with no modes list is draw-only — the shape every
      // signature had before typing was offered.
      draw: !m.signature?.modes?.length || m.signature.modes.includes('draw'),
      type: !!m.signature?.modes?.includes('type'),
      penColor: m.signature?.penColor || '',
      height: m.signature?.height !== undefined ? String(m.signature.height) : '',
      consentText: m.signature?.consentText || '',
      terms: m.signature?.terms || '',
    },
    divider: {
      thickness: m.divider?.thickness !== undefined ? String(m.divider.thickness) : '1',
      color: m.divider?.color || '',
      space: m.divider?.space !== undefined ? String(m.divider.space) : '20',
    },
    heading: { level: m.heading?.level === 2 ? '2' : m.heading?.level === 3 ? '3' : '1' },
    paragraph: { text: m.paragraph?.text || '' },
    image: {
      url: m.image?.url || '',
      alt: m.image?.alt || '',
      width: m.image?.width ? String(m.image.width) : '',
      align: m.image?.align === 'left' || m.image?.align === 'right' ? m.image.align : 'center',
    },
    payment: {
      amountMode: m.payment?.amountMode === 'field' ? 'field' : 'fixed',
      amountFixed: m.payment?.amountFixed !== undefined ? String(m.payment.amountFixed) : '',
      amountField: m.payment?.amountField || '',
      currency: m.payment?.currency || 'usd',
      description: m.payment?.description || '',
    },
    validation: {
      minLen: m.validation?.minLen !== undefined ? String(m.validation.minLen) : '',
      maxLen: m.validation?.maxLen !== undefined ? String(m.validation.maxLen) : '',
      min: m.validation?.min !== undefined ? String(m.validation.min) : '',
      max: m.validation?.max !== undefined ? String(m.validation.max) : '',
      pattern: m.validation?.pattern || '',
      message: m.validation?.message || '',
      dateMin: m.validation?.dateMin || '',
      dateMax: m.validation?.dateMax || '',
    },
    defaultValue: readDefault(f.input_type, m.defaultValue),
    showWhen: m.showWhen || null,
  };
}

/**
 * A published definition as the canvas spine: the fields in the order they
 * were asked, with each card's marker back where the card sat.
 *
 * Cards published before ordering existed carry no `order`; they land after
 * their container's fields, which is exactly where they used to render.
 */
export function withCardMarkers(
  layout: FormLayout, defs: FieldDefinition[], widgets: FormWidget[] = []
): BField[] {
  const LEGACY = Number.MAX_SAFE_INTEGER / 2;
  const items: { order: number; seq: number; field: BField }[] = [];
  let seq = 0;
  for (const f of defs) {
    if (isMarkerKey(f.key)) continue;
    items.push({ order: f.sort_order ?? 0, seq: seq++, field: fromDefinition(f) });
  }
  for (const s of layout.sections || []) {
    for (const c of s.cards || []) {
      const scope = c.scope || 'order';
      const order = typeof c.order === 'number' && Number.isFinite(c.order)
        ? c.order
        : LEGACY + (c.sortOrder ?? 0);
      // A parent no card in the same container claims is no parent at all —
      // the same fallback the renderer makes, so the canvas agrees with it.
      const parent = (c.parent || '').trim();
      const real = parent && parent !== c.id
        && (s.cards || []).some((x) => x.id === parent && (x.scope || 'order') === scope);
      items.push({ order, seq: seq++, field: mkCardMarker(c.id, cont(s.id, scope, real ? parent : '')) });
    }
    // A nested container sits in its PARENT's body, which is the same section.
    const widget = s.widgetId ? widgets.find((w) => w.id === s.widgetId) : undefined;
    const walk = (n: WidgetNode) => {
      for (const child of n.children || []) {
        const order = typeof child.order === 'number' && Number.isFinite(child.order)
          ? child.order
          : LEGACY + seq;
        items.push({ order, seq: seq++, field: mkNodeMarker(child.scope, cont(s.id, n.scope, '')) });
        walk(child);
      }
    };
    if (widget?.tree) walk(widget.tree);
  }
  return items.sort((a, b) => a.order - b.order || a.seq - b.seq).map((x) => x.field);
}

/**
 * A stored draft, brought up to the current canvas shape.
 *
 * Styles get their defaults filled in, and any card without a marker — every
 * card in a draft saved before cards could be placed — gets one at the end of
 * its container, which is where that draft rendered it.
 */
export function withDraftCards(
  sections: BSection[], fields: BField[], widgets: FormWidget[] = []
): { sections: BSection[]; fields: BField[] } {
  const secs = sections.map((s) => ({
    ...s,
    cards: (s.cards || []).map((c) => ({ ...c, scope: c.scope || 'order', style: { ...defaultStyle(), ...(c.style || {}) } })),
    style: { ...defaultStyle(), ...(s.style || {}) },
  }));
  // A real marker carries its cardId/nodeScope; anything else wearing a marker
  // key came back from a publish that leaked one, and shadows the real thing.
  const kept = fields.filter((f) => isStructural(f) || !isMarkerKey(f.key)).map((f) => ({ ...f }));
  // A draft saved while two cards shared an id carries them both; give the
  // later one an id of its own before anything reads the cards by id.
  const split = splitDuplicateCards(secs as BSection[], kept);
  const cardSecs = split.sections as BSection[];
  const out = split.fields;
  const marked = new Set(out.filter((f) => f.cardId).map((f) => `${f.section}:${f.cardId}`));
  for (const s of cardSecs) {
    for (const c of s.cards) {
      if (marked.has(`${s.id}:${c.id}`)) continue;
      out.push(mkCardMarker(c.id, cont(s.id, c.scope || 'order', '')));
    }
  }
  const nodeMarked = new Set(out.filter((f) => f.nodeScope).map((f) => f.nodeScope!));
  for (const s of cardSecs) {
    const widget = s.widgetId ? widgets.find((w) => w.id === s.widgetId) : undefined;
    const walk = (n: WidgetNode) => {
      for (const child of n.children || []) {
        if (!nodeMarked.has(child.scope)) out.push(mkNodeMarker(child.scope, cont(s.id, n.scope, '')));
        walk(child);
      }
    };
    if (widget?.tree) walk(widget.tree);
  }
  return { sections: cardSecs, fields: out };
}

/** BField -> upsert body for POST /admin/fields (matches the old wizard's output). */
export function toFieldBody(f: BField, sortOrder: number) {
  const ui_meta: Record<string, unknown> = {};
  // Visibility + requiredness are plain answers now; a hidden field simply
  // carries a flag nothing sets, which the engine reads as "not shown".
  if (f.vis === 'shown') ui_meta.alwaysShow = true;
  else ui_meta.showFlag = '__hidden';


  if (f.optionsPreset) ui_meta.optionsPreset = f.optionsPreset;
  if (f.countryField.trim()) ui_meta.countryField = f.countryField.trim();
  if (f.group.trim()) ui_meta.group = f.group.trim();
  if (f.row && f.row.trim()) ui_meta.row = f.row.trim();
  if (f.showWhen) ui_meta.showWhen = f.showWhen;
  if (f.placeholder.trim()) ui_meta.placeholder = f.placeholder.trim();
  if (f.infoText.trim()) ui_meta.infoText = f.infoText.trim();
  if (f.type === 'rating') {
    const top = parseInt(f.ratingMax || '', 10);
    if (Number.isFinite(top) && top >= 1 && top !== 5) ui_meta.ratingMax = Math.min(10, top);
  }
  if (f.type === 'address') {
    const address: Record<string, unknown> = {};
    if ((f.addressDefaultCountry || '').trim()) address.defaultCountry = f.addressDefaultCountry!.trim();
    if (f.addressKind === 'shipping') address.kind = 'shipping';
    if (Object.keys(address).length) ui_meta.address = address;
  }
  if (f.type === 'signature') {
    const sg = f.signature || emptySignature();
    const meta: Record<string, unknown> = {};
    // A field with neither box ticked would be a pad nobody can sign. Draw is
    // the fallback rather than an error: it is what the type means.
    const modes = [...(sg.draw || !sg.type ? ['draw'] : []), ...(sg.type ? ['type'] : [])];
    if (modes.length !== 1 || modes[0] !== 'draw') meta.modes = modes;
    if (sg.penColor.trim()) meta.penColor = sg.penColor.trim();
    const h = parseInt(sg.height || '', 10);
    if (Number.isFinite(h) && h >= 80 && h <= 400) meta.height = h;
    if (sg.consentText.trim()) meta.consentText = sg.consentText.trim().slice(0, 500);
    if (sg.terms.trim()) meta.terms = sg.terms.trim().slice(0, 64 * 1024);
    if (Object.keys(meta).length) ui_meta.signature = meta;
  }
  if (f.type === 'divider') {
    const d = f.divider || emptyDivider();
    const num = (v: string, fallback: number) => {
      const n = Number(String(v).trim());
      return Number.isFinite(n) ? n : fallback;
    };
    ui_meta.divider = {
      thickness: Math.max(1, Math.min(8, num(d.thickness, 1))),
      space: Math.max(0, Math.min(80, num(d.space, 20))),
      ...(d.color.trim() ? { color: d.color.trim() } : {}),
    };
  }
  // U5: the content blocks. Each key is written only for its own type, so a
  // block changed into a question (or the reverse) carries nothing stale.
  if (f.type === 'heading') {
    const lv = Number(f.heading?.level);
    ui_meta.heading = { level: lv === 2 ? 2 : lv === 3 ? 3 : 1 };
  }
  if (f.type === 'paragraph') ui_meta.paragraph = { text: (f.paragraph?.text || '').trim() };
  if (f.type === 'image') {
    const im = f.image || emptyImage();
    const width = Math.floor(Number(im.width.trim()));
    ui_meta.image = {
      url: im.url.trim(),
      ...(im.alt.trim() ? { alt: im.alt.trim() } : {}),
      ...(im.width.trim() && Number.isFinite(width) && width > 0 ? { width: Math.min(4000, width) } : {}),
      ...(im.align !== 'center' ? { align: im.align } : {}),
    };
  }
  if (f.type === 'file') {
    const u = f.upload || emptyUpload();
    const upload: Record<string, unknown> = {};
    const int = (s: string) => { const n = Math.floor(Number(s.trim())); return s.trim() && Number.isFinite(n) && n >= 1 ? n : undefined; };
    if (int(u.maxFiles) !== undefined) upload.maxFiles = int(u.maxFiles);
    if (int(u.maxSizeMb) !== undefined) upload.maxSizeMb = int(u.maxSizeMb);
    const accept = u.accept.map((e) => e.trim().toLowerCase().replace(/^\./, '')).filter((e, i, a) => e && a.indexOf(e) === i);
    if (accept.length) upload.accept = accept;
    if (Object.keys(upload).length) ui_meta.upload = upload;
  }
  // U3: an "Other" write-in on a choice field; a US phone format on a tel.
  // Both are written only for the types that read them, so switching a
  // field's type drops a setting it can no longer use.
  if (OPTION_TYPES.includes(f.type as BInputType) && f.allowOther && !f.optionsPreset) {
    const label = (f.otherLabel || '').trim();
    ui_meta.allowOther = label ? { label } : {};
  }
  if (f.type === 'tel' && f.phoneUs) ui_meta.phone = { format: 'us' };
  // P3: each written only for its own type, like the content blocks.
  if (f.type === 'scale') {
    const sc = f.scale || emptyScale();
    const min = sc.min === '0' ? 0 : 1;
    const top = parseInt(sc.max || '', 10);
    ui_meta.scale = {
      min,
      max: Number.isFinite(top) ? Math.max(min + 1, Math.min(10, top)) : 5,
      ...(sc.minLabel.trim() ? { minLabel: sc.minLabel.trim().slice(0, 80) } : {}),
      ...(sc.maxLabel.trim() ? { maxLabel: sc.maxLabel.trim().slice(0, 80) } : {}),
    };
  }
  if (f.type === 'matrix') {
    const mx = f.matrix || emptyMatrix();
    const clean = (list: FieldOption[]) => list
      .map((o) => ({ value: o.value.trim(), text: o.text.trim() || o.value.trim() }))
      .filter((o, i, a) => o.value && a.findIndex((x) => x.value === o.value) === i);
    ui_meta.matrix = { rows: clean(mx.rows), columns: clean(mx.columns), ...(mx.multi ? { multi: true } : {}) };
  }
  if (f.type === 'payment') {
    const p = f.payment || emptyPayment();
    const amountFixed = Number(p.amountFixed);
    ui_meta.payment = {
      amountMode: p.amountMode,
      ...(p.amountMode === 'fixed' && p.amountFixed.trim() && Number.isFinite(amountFixed) ? { amountFixed } : {}),
      ...(p.amountMode === 'field' && p.amountField ? { amountField: p.amountField } : {}),
      currency: p.currency || 'usd',
      ...(p.description.trim() ? { description: p.description.trim() } : {}),
    };
  }
  const v = f.validation;
  if (v) {
    const validation: Record<string, unknown> = {};
    const num = (s: string) => { const n = Number(s.trim()); return s.trim() && Number.isFinite(n) ? n : undefined; };
    if (num(v.minLen) !== undefined) validation.minLen = num(v.minLen);
    if (num(v.maxLen) !== undefined) validation.maxLen = num(v.maxLen);
    if (num(v.min) !== undefined) validation.min = num(v.min);
    if (num(v.max) !== undefined) validation.max = num(v.max);
    if (v.pattern.trim()) validation.pattern = v.pattern.trim();
    if (v.message.trim()) validation.message = v.message.trim();
    // A date window only means anything on a date (or date-time, P3) field, so it only ships on one.
    if (f.type === 'date' || f.type === 'datetime') {
      if (v.dateMin.trim()) validation.dateMin = v.dateMin.trim();
      if (v.dateMax.trim()) validation.dateMax = v.dateMax.trim();
    }
    if (Object.keys(validation).length) ui_meta.validation = validation;
  }
  const dflt = writeDefault(f.type, f.defaultValue);
  if (dflt !== undefined) ui_meta.defaultValue = dflt;
  if (f.lock === 'locked') ui_meta.locked = true;

  return {
    key: f.key.trim(),
    label: f.label.trim(),
    section: f.section,
    input_type: f.type,
    scope: f.scope,
    flow_applicability: f.flow,
    sort_order: sortOrder,
    required_default: f.req === 'whenShown',
    // An option's picture (P3) only means anything on the two tile-able types,
    // and its points only on the types where one option is the answer.
    options: OPTION_TYPES.includes(f.type as BInputType)
      ? f.options.filter((o) => o.value.trim() || o.text.trim())
        .map((o) => ({
          value: o.value,
          text: o.text,
          ...(IMAGE_OPTION_TYPES.includes(f.type as BInputType) && o.image?.trim() ? { image: o.image.trim() } : {}),
          ...(SCORE_TYPES.includes(f.type as BInputType) && typeof o.score === 'number' && Number.isFinite(o.score)
            ? { score: o.score } : {}),
          ...(SCORE_TYPES.includes(f.type as BInputType) && o.exempt ? { exempt: true } : {}),
        }))
      : [],
    ui_meta,
  };
}

/**
 * Which field types can start from an answer.
 *
 * A composite, a file and a payment cannot: their value is not a thing anyone
 * would type into a settings panel, and a half-filled address is worse than a
 * blank one. A divider has no answer at all.
 */
export const DEFAULTABLE: BInputType[] = [
  'text', 'textarea', 'email', 'tel', 'url', 'number', 'date', 'time',
  'select', 'radio', 'multiselect', 'checkbox', 'rating', 'scale',
];
export const takesDefault = (type: BField['type']): boolean =>
  DEFAULTABLE.includes(type as BInputType);

/** A stored ui_meta.defaultValue as the panel edits it. */
export function readDefault(type: string, raw: unknown): string | string[] | boolean | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (type === 'multiselect') return Array.isArray(raw) ? raw.map(String) : [String(raw)];
  if (type === 'checkbox') return raw === true || raw === 'true';
  if (Array.isArray(raw)) return raw.length ? String(raw[0]) : undefined;
  return String(raw);
}

/** …and back, or undefined when the field simply starts blank. */
export function writeDefault(type: BField['type'], v: BField['defaultValue']): string | string[] | boolean | undefined {
  if (!takesDefault(type)) return undefined;
  if (type === 'checkbox') return v === true ? true : undefined;
  if (type === 'multiselect') {
    const list = (Array.isArray(v) ? v : []).map(String).filter(Boolean);
    return list.length ? list : undefined;
  }
  const s = typeof v === 'string' ? v.trim() : '';
  return s || undefined;
}

/** A freshly dropped palette field: shown, required whenever shown. `preset` is the NPS flavour of a scale. */
export function newField(type: BInputType, fields: { key: string }[], reserved: Iterable<string> = [], preset?: 'nps'): BField {
  const names: Record<string, string> = {
    text: 'New Text Field', textarea: 'New Text Area', select: 'New Dropdown', checkbox: 'New Checkbox',
    email: 'New Email Field', tel: 'New Phone Field', date: 'New Date Field', number: 'New Number Field',
    file: 'New File Upload', radio: 'New Multiple Choice', multiselect: 'New Checkboxes',
    rating: 'New Rating', time: 'New Time Field', url: 'New Website Field',
    payment: 'Payment', name: 'Name', address: 'Address', divider: 'Divider',
    heading: 'Heading', paragraph: 'Paragraph', image: 'Image',
    scale: 'New Linear Scale', matrix: 'New Matrix', ranking: 'New Ranking', datetime: 'New Date and Time',
  };
  const label = preset === 'nps' ? 'How likely are you to recommend us to a friend or colleague?' : names[type] || 'New Field';
  const key = deriveKeyIn(label, fields, reserved);
  return {
    key, label, section: '', group: '', row: null, type, scope: 'order', flow: 'both',
    vis: 'shown', req: type === 'payment' || PRESENTATION.has(type) ? 'never' : 'whenShown', lock: 'editable',
    optionsPreset: '', countryField: '', showWhen: null, placeholder: '', infoText: '',
    ratingMax: '', validation: emptyValidation(), payment: emptyPayment(), divider: emptyDivider(),
    heading: emptyHeading(), paragraph: emptyParagraph(), image: emptyImage(),
    signature: emptySignature(), upload: emptyUpload(),
    allowOther: false, otherLabel: '', phoneUs: false,
    scale: preset === 'nps' ? npsScale() : emptyScale(), matrix: emptyMatrix(),
    options: type === 'ranking'
      ? [{ value: 'option1', text: 'Option 1' }, { value: 'option2', text: 'Option 2' }, { value: 'option3', text: 'Option 3' }]
      : OPTION_TYPES.includes(type)
        ? [{ value: 'option1', text: 'Option 1' }, { value: 'option2', text: 'Option 2' }]
        : [],
    isNew: true,
  };
}

/**
 * Rename a field's key everywhere the form points at it.
 *
 * A key is not just a label: rules match on it, calculations read it, one
 * field's country list names another, a widget titles its instances from one,
 * and a payment takes its amount from one. Renaming the field alone would
 * leave all of those pointing at a key that no longer exists — quietly, and
 * only visible once the form was live. So the rename goes everywhere at once.
 *
 * Composite sub-paths ('shipTo.city') follow their parent.
 */
export function renameFieldKey<D extends Record<string, unknown> | undefined = undefined>(
  from: string, to: string,
  parts: { fields: BField[]; rules: FormRule[]; widgets: FormWidget[]; document?: D }
): { fields: BField[]; rules: FormRule[]; widgets: FormWidget[]; document: D } {
  const at = (k: string): string => {
    if (k === from) return to;
    return k.startsWith(`${from}.`) ? to + k.slice(from.length) : k;
  };
  const fields = parts.fields.map((f) => ({
    ...f,
    ...(f.key === from ? { key: to } : {}),
    ...(f.countryField === from ? { countryField: to } : {}),
    ...(f.showWhen && f.showWhen.field === from ? { showWhen: { ...f.showWhen, field: to } } : {}),
    ...(f.payment?.amountField === from ? { payment: { ...f.payment, amountField: to } } : {}),
  }));
  const rules = parts.rules.map((r): FormRule => ({
    ...r,
    when: (r.when || []).map((w) => ({ ...w, field: at(w.field) })),
    actions: (r.actions || []).map((a) => {
      if (a.type === 'prefill') {
        // `value` is a literal, not a key — renaming a field must not touch it.
        return { ...a, map: (a.map || []).map((m) => ({ ...m, to: at(m.to), ...(m.from ? { from: at(m.from) } : {}) })) };
      }
      if (a.type === 'calc') {
        const c = a.calc;
        const calc = c.type === 'sum' ? { ...c, field: at(c.field) }
          : c.type === 'math' ? { ...c, a: at(c.a), b: at(c.b) }
          : c.type === 'join' ? { ...c, fields: c.fields.map(at) }
          : c;
        return { ...a, target: at(a.target), calc };
      }
      // A goto names a page, not a field.
      if (a.type === 'goto') return a;
      // 'sec:' / 'card:' / 'wnode:' targets are containers, not fields: `at`
      // leaves them alone.
      return { ...a, targets: (a.targets || []).map(at) };
    }),
  }));
  const renameNode = (n: WidgetNode): WidgetNode => ({
    ...n,
    ...(n.summaryField === from ? { summaryField: to } : {}),
    // A conditional count names the questions it reads, like a rule's `when`.
    ...(n.counts ? { counts: n.counts.map((c) => ({ ...c, when: (c.when || []).map((w) => ({ ...w, field: at(w.field) })) })) } : {}),
    children: (n.children || []).map(renameNode),
  });
  const widgets = parts.widgets.map((w) => (w.tree ? { ...w, tree: renameNode(w.tree) } : w));
  // The document (R5): placements, regions, the AcroForm mapping and the
  // parts follow the key too. It never rides the draft, so the builder
  // records the rename instead (`FormDraft.renames`) and the publish route
  // applies this same function's server twin when the new key goes live;
  // this branch is for a caller that holds the document in hand.
  const document = (parts.document
    ? renameKeyInDocument(parts.document as Record<string, unknown>, from, to)
    : parts.document) as D;
  return { fields, rules, widgets, document };
}

/**
 * What stops a key from being used: its shape, or something else on the form
 * already holding it. Returns the problem in the words the panel shows, or null.
 *
 * "Something else" is wider than the other questions. A marker key belongs to
 * the canvas — a question wearing one shadows the card or container it names,
 * and is dropped as an impostor the next time the form is opened. A count key
 * lands in the same flat submission object as an answer, so a question sharing
 * one has its answer overwritten by the count on the way out.
 */
export function keyProblem(key: string, fields: BField[], own: string, widgets: FormWidget[] = []): string | null {
  const k = key.trim();
  if (!k) return 'A key cannot be empty.';
  if (!/^[a-zA-Z0-9_-]+$/.test(k)) return 'Letters, numbers, hyphen and underscore only.';
  if (isMarkerKey(k)) return 'That prefix is kept for cards and widget containers.';
  if (k.startsWith('_')) return 'A key cannot start with an underscore: that prefix is kept for the server records.';
  if (k !== own && fields.some((f) => !isStructural(f) && f.key === k)) return 'Another field already uses this key.';
  if (countKeysOf(widgets).has(k)) return 'A widget already counts itself with this key.';
  return null;
}

/**
 * Every count key the widgets on this form publish: each container's
 * automatic one, and the key of each of its conditional counts.
 */
export const countKeysOf = (widgets: FormWidget[]): Set<string> =>
  new Set(allNodes(widgets).flatMap((n) => [n.countKey, ...(n.counts || []).map((c) => c.key)]).filter(Boolean));

/**
 * A count key nothing on the form is publishing yet.
 *
 * `taken` is the count keys AND the field keys: both end up in one flat
 * submission object, where the later write silently wins.
 */
export function freeCountKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/** What stops a count key from being used. Same namespace as a field key. */
export function countKeyProblem(
  key: string, scope: string, widgets: FormWidget[], fields: BField[]
): string | null {
  const k = key.trim();
  if (!k) return 'A count key cannot be empty.';
  if (!/^[a-zA-Z0-9_-]+$/.test(k)) return 'Letters, numbers, hyphen and underscore only.';
  if (isMarkerKey(k)) return 'That prefix is kept for cards and widget containers.';
  if (fields.some((f) => !isStructural(f) && f.key === k)) return 'A question already uses this key.';
  if (allNodes(widgets).some((n) => n.scope !== scope && n.countKey === k)) {
    return 'Another container already counts itself with this key.';
  }
  if (allNodes(widgets).some((n) => (n.counts || []).some((c) => c.key === k))) {
    return 'A conditional count already uses this key.';
  }
  return null;
}

/**
 * What stops a CONDITIONAL count's key from being used — the count at `index`
 * on the container `scope`. One namespace again: the questions, every
 * container's automatic count, and every other conditional count.
 */
export function conditionalCountKeyProblem(
  key: string, scope: string, index: number, widgets: FormWidget[], fields: BField[]
): string | null {
  const k = key.trim();
  if (!k) return 'A count key cannot be empty.';
  if (!/^[a-zA-Z0-9_-]+$/.test(k)) return 'Letters, numbers, hyphen and underscore only.';
  if (isMarkerKey(k)) return 'That prefix is kept for cards and widget containers.';
  if (k.startsWith('_')) return 'A key cannot start with an underscore: that prefix is kept for the server records.';
  if (fields.some((f) => !isStructural(f) && f.key === k)) return 'A question already uses this key.';
  for (const n of allNodes(widgets)) {
    if (n.countKey === k) return 'A container already counts itself with this key.';
    if ((n.counts || []).some((c, i) => c.key === k && !(n.scope === scope && i === index))) {
      return 'Another conditional count already uses this key.';
    }
  }
  return null;
}

/** The key a new conditional count starts with, until its label names it. */
const PLACEHOLDER_COUNT_KEY = /^conditionalCount\d*$/;

/** A fresh conditional count for a container, on a key nothing else holds. */
export function newConditionalCount(widgets: FormWidget[], fields: BField[]): WidgetCount {
  const key = deriveKeyIn('conditional count', fields.filter((f) => !isStructural(f)), countKeysOf(widgets));
  return { key, label: '', match: 'all', when: [] };
}

/**
 * The key a conditional count should take when its LABEL changes: one derived
 * from the label while the key is still the placeholder it was minted with,
 * and the key it already has once somebody has chosen one — an integration
 * may be mapping it by then.
 */
export function conditionalCountKeyFor(
  count: WidgetCount, label: string, widgets: FormWidget[], fields: BField[]
): string {
  if (!PLACEHOLDER_COUNT_KEY.test(count.key) || !label.trim()) return count.key;
  const taken = new Set(countKeysOf(widgets));
  taken.delete(count.key);
  return deriveKeyIn(label, fields.filter((f) => !isStructural(f)), taken);
}

// ---- widget trees ------------------------------------------------------------
// The repeatable containers, edited on the canvas rather than on a tab of
// their own. Everything here is pure so the drag gestures that reshape a tree
// (nest, promote, absorb) can be tested without a DOM.

/** Root + three embedded levels. MUST match MAX_WIDGET_DEPTH on the server. */
export const MAX_WIDGET_DEPTH = 4;

export const cloneNode = (n: WidgetNode): WidgetNode => ({ ...n, children: (n.children || []).map(cloneNode) });
export const cloneWidget = (w: FormWidget): FormWidget => ({ ...w, tree: cloneNode(w.tree) });

/** Every node of a tree, root first. */
export function nodesOf(n: WidgetNode): WidgetNode[] {
  return [n, ...(n.children || []).flatMap(nodesOf)];
}

/** Every node across every widget on the form. */
export const allNodes = (widgets: FormWidget[]): WidgetNode[] =>
  widgets.flatMap((w) => (w.tree ? nodesOf(w.tree) : []));

/** The widget holding `scope`, and the node itself. */
export function findNode(widgets: FormWidget[], scope: string): { widget: FormWidget; node: WidgetNode } | null {
  for (const w of widgets) {
    if (!w.tree) continue;
    const node = nodesOf(w.tree).find((n) => n.scope === scope);
    if (node) return { widget: w, node };
  }
  return null;
}

/** How deep `scope` sits in its tree (0 = the root). -1 when it is not there. */
export function depthOf(tree: WidgetNode, scope: string, depth = 0): number {
  if (tree.scope === scope) return depth;
  for (const c of tree.children || []) {
    const d = depthOf(c, scope, depth + 1);
    if (d >= 0) return d;
  }
  return -1;
}

/** Levels a subtree occupies: a childless node is 1. */
export const heightOf = (n: WidgetNode): number =>
  1 + (n.children || []).reduce((acc, c) => Math.max(acc, heightOf(c)), 0);

/** Cut `scope` out of a tree, returning the pruned tree and the subtree taken. */
export function detachNode(tree: WidgetNode, scope: string): { tree: WidgetNode; taken: WidgetNode | null } {
  let taken: WidgetNode | null = null;
  const prune = (n: WidgetNode): WidgetNode => {
    const kids: WidgetNode[] = [];
    for (const c of n.children || []) {
      if (c.scope === scope) { taken = c; continue; }
      kids.push(prune(c));
    }
    return { ...n, children: kids };
  };
  return { tree: prune(tree), taken };
}

/** Graft `child` onto the node named `parentScope`. */
export function attachChild(tree: WidgetNode, parentScope: string, child: WidgetNode): WidgetNode {
  const walk = (n: WidgetNode): WidgetNode => (n.scope === parentScope
    ? { ...n, children: [...(n.children || []), child] }
    : { ...n, children: (n.children || []).map(walk) });
  return walk(tree);
}

/** Patch one node in place, leaving the rest of the tree alone. */
export function patchNodeIn(tree: WidgetNode, scope: string, patch: Partial<WidgetNode>): WidgetNode {
  const walk = (n: WidgetNode): WidgetNode => (n.scope === scope
    ? { ...n, ...patch, children: (n.children || []).map(walk) }
    : { ...n, children: (n.children || []).map(walk) });
  return walk(tree);
}

/**
 * A scope no widget on the form is using yet, plus the counter to carry
 * forward. Scopes are unique per FORM, not per widget — the server enforces
 * the same rule when it sanitizes.
 */
export function uniqueScope(widgets: FormWidget[], from: number): { scope: string; num: number } {
  const taken = new Set(allNodes(widgets).map((n) => n.scope));
  let num = from;
  let scope: string;
  do { num++; scope = 'item' + num; } while (taken.has(scope));
  return { scope, num };
}

/**
 * A fresh container, the shape a drop from the palette produces.
 *
 * `num` only ever guaranteed the SCOPE was free, so `itemCount${num}` could
 * still land on a count key — or a field key — the form was already using.
 * Pass what is taken and the count key steps around it.
 */
export const newWidgetNode = (scope: string, num: number, taken: Iterable<string> = []): WidgetNode => ({
  scope, itemLabel: 'Item', addLabel: '+ Add Item', min: 1, max: 0,
  countKey: freeCountKey(`itemCount${num}`, new Set(taken)), summaryField: '', children: [],
});

/**
 * Move everything belonging to `scopes` into one section.
 *
 * Dragging a container out of one section and into another (nesting it,
 * or promoting it back to the top) takes its contents with it: the fields that
 * repeat inside it and the cards that group them. Both name their section, so
 * both are re-pointed here — one function, so a gesture can never move the
 * container and leave its cards behind.
 *
 * A card id that already exists in the destination is renamed on the way in,
 * and the fields grouped by it follow the new id. Card ids only have to be
 * unique within their section, so two sections colliding is ordinary.
 */
export function moveScopePieces(
  sections: BSection[], fields: BField[], scopes: Set<string>, toSecId: string
): { sections: BSection[]; fields: BField[] } {
  const taken = new Set(sections.find((s) => s.id === toSecId)?.cards.map((c) => c.id) || []);
  /** old card id -> the id it landed under */
  const renamed = new Map<string, string>();
  const moving: BCard[] = [];

  const kept = sections.map((s) => {
    if (s.id === toSecId) return s;
    const stay: BCard[] = [];
    for (const c of s.cards) {
      if (!scopes.has(c.scope)) { stay.push(c); continue; }
      let id = c.id;
      while (taken.has(id)) id = `${id}_`;
      taken.add(id);
      if (id !== c.id) renamed.set(c.id, id);
      moving.push({ ...c, id });
    }
    return stay.length === s.cards.length ? s : { ...s, cards: stay };
  });

  return {
    sections: kept.map((s) => (s.id === toSecId && moving.length ? { ...s, cards: [...s.cards, ...moving] } : s)),
    // Card markers travel with the container exactly like the fields do: a
    // card whose id was renamed on the way in keeps its place, and so does
    // every field and card that pointed at it.
    fields: fields.map((f) => (scopes.has(f.scope)
      ? {
        ...f,
        section: toSecId,
        group: renamed.get(f.group) ?? f.group,
        ...(f.cardId ? { cardId: renamed.get(f.cardId) ?? f.cardId, key: `__card_${renamed.get(f.cardId) ?? f.cardId}` } : {}),
      }
      : f)),
  };
}

/**
 * Move a card — and every card nested inside it, with all their fields — into
 * another container.
 *
 * Cards are addressed by id within a SECTION, so crossing sections can collide
 * with an id already there; a collision renames on the way in and everything
 * pointing at the old id follows, exactly as it does when a widget moves. The
 * root card's own place among its new siblings is not decided here — the drop
 * gesture positions its marker afterwards.
 */
export function relocateCard(
  sections: BSection[], fields: BField[], from: { sec: string; id: string }, to: Cont
): { sections: BSection[]; fields: BField[]; id: string } {
  const ids = cardAndDescendants(fields, from.sec, from.id);
  if (from.sec === to.sec) {
    // Same section: only the scope can have changed (body → widget, say), and
    // the card records carry it alongside their fields.
    return {
      sections: sections.map((s) => (s.id === to.sec
        ? { ...s, cards: s.cards.map((c) => (ids.has(c.id) ? { ...c, scope: to.scope } : c)) }
        : s)),
      fields: fields.map((f) => ((f.cardId && ids.has(f.cardId)) || (f.group && ids.has(f.group))
        ? { ...f, scope: to.scope, ...(to.scope !== 'order' ? { flow: 'complex' as const } : {}) }
        : f)),
      id: from.id,
    };
  }

  const taken = new Set(sections.find((s) => s.id === to.sec)?.cards.map((c) => c.id) || []);
  const renamed = new Map<string, string>();
  const moving: BCard[] = [];
  const kept = sections.map((s) => {
    if (s.id !== from.sec) return s;
    const stay: BCard[] = [];
    for (const c of s.cards) {
      if (!ids.has(c.id)) { stay.push(c); continue; }
      let id = c.id;
      while (taken.has(id)) id = `${id}_`;
      taken.add(id);
      if (id !== c.id) renamed.set(c.id, id);
      moving.push({ ...c, id, scope: to.scope });
    }
    return { ...s, cards: stay };
  });
  const rename = (id: string) => renamed.get(id) ?? id;
  return {
    sections: kept.map((s) => (s.id === to.sec ? { ...s, cards: [...s.cards, ...moving] } : s)),
    fields: fields.map((f) => {
      const inside = (f.cardId && ids.has(f.cardId)) || (f.group && ids.has(f.group));
      if (!inside) return f;
      const cardId = f.cardId ? rename(f.cardId) : undefined;
      return {
        ...f,
        section: to.sec,
        scope: to.scope,
        ...(to.scope !== 'order' ? { flow: 'complex' as const } : {}),
        group: f.group && ids.has(f.group) ? rename(f.group) : f.group,
        ...(cardId ? { cardId, key: `__card_${cardId}` } : {}),
      };
    }),
    id: rename(from.id),
  };
}

/**
 * How many levels a card's own stack occupies — 1 for a card with no cards in
 * it. What a container can accept is MAX_CARD_DEPTH minus what it already is.
 */
export function cardHeight(fields: BField[], secId: string, cardId: string): number {
  const kids = fields.filter((f) => f.cardId && f.section === secId && f.group === cardId);
  return 1 + kids.reduce((acc, k) => Math.max(acc, cardHeight(fields, secId, k.cardId!)), 0);
}

// ---- preview lens (named flag presets, builder-only) ------------------------
export interface Lens { id: string; name: string; flow: '' | 'simple' | 'complex'; cfg: FormConfig }

/** Mirrors form-engine/config.ts fieldVisible on the BField model. */
export function visibleUnder(f: BField, lens: Lens): boolean {
  if (f.flow !== 'both' && lens.flow && f.flow !== lens.flow) return false;
  return f.vis === 'shown';
}

// ---- container / row helpers -------------------------------------------------
/**
 * Where a field sits on the canvas. Three coordinates, because a field can be
 * inside a repeatable widget AND inside a card within it at the same time:
 *
 *   sec   — the section
 *   scope — 'order' (asked once) or the widget node it repeats inside
 *   grp   — '' (the container's own body) or the id of a card within it
 *
 * A field in a plain section is always order-scoped whatever its stored scope
 * says: a scope left behind by a deleted widget must not strand the field.
 */
export interface Cont { sec: string; scope: string; grp: string }

export const cont = (sec: string, scope: string, grp: string): Cont => ({ sec, scope, grp });

export function contOf(f: BField, widgetSectionIds: Set<string>): Cont {
  return {
    sec: f.section,
    scope: widgetSectionIds.has(f.section) ? f.scope : 'order',
    grp: f.group || '',
  };
}

export const sameCont = (a: Cont, b: Cont) => a.sec === b.sec && a.scope === b.scope && a.grp === b.grp;

/** A stable string for a container — React keys and drop-target comparisons. */
export const contKey = (c: Cont) => `${c.sec}|${c.scope}|${c.grp}`;

export function inCont(fields: BField[], c: Cont, widgetSectionIds: Set<string>): BField[] {
  return fields.filter((f) => sameCont(contOf(f, widgetSectionIds), c));
}

/** Rewrite a field's own coordinates so it belongs to `c`. */
export function assignCont(f: BField, c: Cont) {
  f.section = c.sec;
  f.group = c.grp;
  if (c.scope !== 'order') {
    f.scope = c.scope;
    f.flow = 'complex';
  } else if (f.scope !== 'order') {
    f.scope = 'order';
    if (f.flow === 'complex') f.flow = 'both';
  }
}

/** Group adjacent fields sharing a row label into render runs. */
export function pack(items: BField[]): BField[][] {
  const rows: BField[][] = [];
  let i = 0;
  while (i < items.length) {
    const f = items[i];
    if (f.row) { const run = [f]; while (i + 1 < items.length && items[i + 1].row === f.row) run.push(items[++i]); rows.push(run); }
    else rows.push([f]);
    i++;
  }
  return rows;
}

// ---- rule sentences (shared by the builder panel + test mode) ----------------
const OP_TEXT: Record<string, string> = {
  is: 'is', isnot: 'is not', includes: 'includes', notincludes: 'does not include',
  filled: 'is filled in', empty: 'is empty',
  gt: 'is more than', lt: 'is less than', gte: 'is at least', lte: 'is at most',
  before: 'is before', after: 'is after', contains: 'contains',
};

/**
 * One-line plain-English summary of a rule (its trigger → every action).
 *
 * `fields` is what lets a condition read back in the words the form actually
 * uses: a choice question stores an option's value and shows its label, and a
 * summary quoting the value describes the rule in a vocabulary the person who
 * wrote it was never shown. Pass none and values read as themselves.
 */
export function ruleText(
  r: FormRule, lbl: (k: string) => string, fields: BField[] = [], pageLbl?: (id: string) => string
): string {
  const valLbl = (key: string, value: string) =>
    fields.find((f) => f.key === key)?.options?.find((o) => o.value === value)?.text || value;
  const conds = (r.when || []).map((c) => {
    const op = OP_TEXT[c.op] || 'is';
    return c.op === 'filled' || c.op === 'empty'
      ? `"${lbl(c.field)}" ${op}`
      : `"${lbl(c.field)}" ${op} "${valLbl(c.field, c.value ?? '')}"`;
  });
  const when = r.trigger === 'always' ? 'Always'
    : conds.length ? `When ${conds.join(r.match === 'any' ? ' OR ' : ' AND ')}`
    : 'When (no conditions yet — never runs)';
  const acts = (r.actions || []).map((a) => actionText(a, lbl, pageLbl));
  return `${when} → ${acts.length ? acts.join('; ') : '(no actions yet)'}`;
}

/**
 * The pages of the canvas, as a `goto` picker and a rule sentence name them:
 * "Page 2 — Your details" (the page title, else the title of the section
 * that starts it). Same split as the renderer's `derivePages`: the first
 * section starts page 1 and every `pageBreak` starts another.
 */
export function pageOptions(
  sections: { id: string; title?: string; pageBreak?: boolean; pageTitle?: string }[]
): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  sections.forEach((s, i) => {
    if (i > 0 && !s.pageBreak) return;
    const n = out.length + 1;
    const name = (s.pageTitle || s.title || '').trim();
    out.push({ id: s.id, label: `Page ${n}${name ? ` — ${name}` : ''}` });
  });
  return out;
}

/**
 * The pages a flat section list makes (R4): the first section starts page 1,
 * and every section with `pageBreak` starts the next. `start`/`end` index
 * `sections` (end exclusive); `id` is the section the page starts with, which
 * is where its title lives.
 */
export interface PageSpan { n: number; id: string; title: string; start: number; end: number }
export function pagesOf(
  sections: { id: string; title?: string; pageBreak?: boolean; pageTitle?: string }[]
): PageSpan[] {
  const out: PageSpan[] = [];
  sections.forEach((s, i) => {
    if (i > 0 && !s.pageBreak) { out[out.length - 1].end = i + 1; return; }
    out.push({ n: out.length + 1, id: s.id, title: (s.pageTitle || '').trim(), start: i, end: i + 1 });
  });
  return out;
}

/**
 * The pill on a rule wherever one is listed outside the Logic page.
 *
 * There is only one rule kind now, so the pill says the only thing still worth
 * distinguishing: whether the rule waits for an answer, or runs on every form.
 */
export const ruleTriggerChip = (r: FormRule): { label: string; bg: string; fg: string } => (
  r.trigger === 'always'
    ? { label: 'Always', bg: '#edf2f7', fg: '#4a5568' }
    : { label: 'Conditional', bg: '#ebf4ff', fg: '#4c51bf' }
);

// ---- builder ergonomics (U4): duplicate, paste a list, move by keyboard -------

/** `email_2`, `email_3`… — a key nothing on the form answers to yet. */
export function freeFieldKey(base: string, taken: Set<string>): string {
  // Duplicating a duplicate counts on from the same root: `line_2` → `line_3`,
  // not `line_2_2`.
  const m = /^(.+?)_(\d+)$/.exec(base);
  const root = m ? m[1] : base;
  let n = m ? Number(m[2]) + 1 : 2;
  while (taken.has(`${root}_${n}`)) n += 1;
  return `${root}_${n}`;
}

export { freeCardId };

/** An id no section is using — sections and widgets are addressed by id. */
export function freeSectionId(base: string, secs: { id: string }[]): string {
  const taken = new Set(secs.map((x) => x.id));
  let id = base, i = 2;
  while (taken.has(id)) id = base + i++;
  return id;
}

/** Every key a new field must clear: the questions and the widget count keys. */
const keysTaken = (fields: BField[], widgets: FormWidget[]): Set<string> =>
  new Set([...fields.map((f) => f.key), ...countKeysOf(widgets)]);

/**
 * A field as a copy of another: same settings, a fresh key, its own object
 * all the way down (options, validation…) so an edit to one never leaks into
 * the other. It is not `isNew` — typing a label into the copy must not re-mint
 * its key the way it does for a field just dropped from the palette.
 */
export function cloneField(f: BField, key: string): BField {
  const copy = JSON.parse(JSON.stringify(f)) as BField;
  delete copy.id;
  copy.key = key;
  copy.isNew = false;
  copy.keyTouched = true;
  return copy;
}

/** The fields sitting in the same row as `f` (itself included), in order. */
function rowRun(items: BField[], f: BField): BField[] {
  const i = items.indexOf(f);
  if (i < 0 || !f.row) return [f];
  let s = i, e = i;
  while (s > 0 && items[s - 1].row === f.row) s--;
  while (e < items.length - 1 && items[e + 1].row === f.row) e++;
  return items.slice(s, e + 1);
}

/**
 * The same field again, right under the original.
 *
 * Under, not beside: a copy squeezed into the original's row would overflow
 * it, and "duplicate" means "one more of these", not "widen this row". Rules
 * are keyed by field key, so the copy has none — the caller says so.
 */
export function duplicateField(
  fields: BField[], key: string, widgets: FormWidget[], widgetSecIds: Set<string>
): { fields: BField[]; key: string } | null {
  const src = fields.find((f) => f.key === key);
  if (!src || isStructural(src)) return null;
  const copy = cloneField(src, freeFieldKey(src.key, keysTaken(fields, widgets)));
  copy.row = null;
  const items = inCont(fields, contOf(src, widgetSecIds), widgetSecIds);
  const run = rowRun(items, src);
  const at = fields.indexOf(run[run.length - 1]) + 1;
  const out = fields.slice();
  out.splice(at, 0, copy);
  return { fields: out, key: copy.key };
}

/**
 * Point a copied field's references at the copied fields, where it had them.
 * A reference to something outside the copy is left alone: it still exists.
 */
export function remapRefs(f: BField, keys: Map<string, string>): void {
  if (f.countryField && keys.has(f.countryField)) f.countryField = keys.get(f.countryField)!;
  if (f.showWhen?.field && keys.has(f.showWhen.field)) f.showWhen = { ...f.showWhen, field: keys.get(f.showWhen.field)! };
  if (f.payment?.amountField && keys.has(f.payment.amountField)) f.payment = { ...f.payment, amountField: keys.get(f.payment.amountField)! };
}

/**
 * Copy a block of fields — a card's contents, a section's — with fresh keys
 * and card ids, keeping their order and their references to one another.
 * Widget containers are never copied (`skipped` counts the ones left out):
 * their clone path has different semantics and lives elsewhere.
 */
function copyBlock(
  block: BField[], fields: BField[], widgets: FormWidget[],
  cardIds: Map<string, string>, secId: (old: string) => string
): { copies: BField[]; keys: Map<string, string>; skipped: number } {
  const taken = keysTaken(fields, widgets);
  const keys = new Map<string, string>();
  const copies: BField[] = [];
  let skipped = 0;
  for (const f of block) {
    if (f.nodeScope) { skipped += 1; continue; }
    let key: string;
    if (f.cardId) key = `__card_${cardIds.get(f.cardId) || f.cardId}`;
    else if (f.slot) key = `__${freeFieldKey(f.key.replace(/^__/, ''), new Set([...taken].map((k) => k.replace(/^__/, ''))))}`;
    else { key = freeFieldKey(f.key, taken); keys.set(f.key, key); }
    taken.add(key.replace(/^__/, ''));
    const c = cloneField(f, key);
    if (c.cardId) c.cardId = cardIds.get(c.cardId) || c.cardId;
    if (c.group && cardIds.has(c.group)) c.group = cardIds.get(c.group)!;
    c.section = secId(c.section);
    copies.push(c);
  }
  for (const c of copies) remapRefs(c, keys);
  return { copies, keys, skipped };
}

/** A card and every card nested in it, by id, in `sec`. */
function cardTree(fields: BField[], secId: string, cardId: string): string[] {
  const out = [cardId];
  for (let i = 0; i < out.length; i++) {
    for (const f of fields) if (f.cardId && f.section === secId && f.group === out[i]) out.push(f.cardId);
  }
  return out;
}

/**
 * The same card again — title, style, every field in it and every card
 * nested in it — right under the original, with ids and keys of its own.
 */
export function duplicateCard(
  sections: BSection[], fields: BField[], widgets: FormWidget[], secId: string, cardId: string
): { sections: BSection[]; fields: BField[]; id: string; keys: Map<string, string>; skipped: number } | null {
  const sec = sections.find((s) => s.id === secId);
  const card = sec?.cards.find((c) => c.id === cardId);
  const marker = markerOf(fields, secId, cardId);
  if (!sec || !card || !marker) return null;

  const allCardIds = new Set(sections.flatMap((s) => s.cards.map((c) => c.id)));
  const ids = cardTree(fields, secId, cardId);
  const cardIds = new Map<string, string>();
  for (const id of ids) { const fresh = freeCardId(id, allCardIds); allCardIds.add(fresh); cardIds.set(id, fresh); }

  const inTree = new Set(ids);
  const block = fields.filter((f) => f === marker || (f.section === secId && !!f.group && inTree.has(f.group)));
  const { copies, keys, skipped } = copyBlock(block, fields, widgets, cardIds, (s) => s);
  const at = Math.max(...block.map((f) => fields.indexOf(f))) + 1;
  const outFields = fields.slice();
  outFields.splice(at, 0, ...copies);

  const newCards: BCard[] = ids
    .map((id) => sec.cards.find((c) => c.id === id))
    .filter((c): c is BCard => !!c)
    .map((c) => ({ ...c, id: cardIds.get(c.id)!, style: { ...c.style } }));
  const ci = sec.cards.indexOf(card);
  const cards = sec.cards.slice();
  cards.splice(ci + 1, 0, ...newCards);
  const outSections = sections.map((s) => (s.id === secId ? { ...s, cards } : s));
  return { sections: outSections, fields: outFields, id: cardIds.get(cardId)!, keys, skipped };
}

/**
 * The same section again, right under the original. A widget section is
 * refused (null): it IS its repeating container, and containers are not
 * duplicated here.
 */
export function duplicateSection(
  sections: BSection[], fields: BField[], widgets: FormWidget[], secId: string
): { sections: BSection[]; fields: BField[]; id: string; keys: Map<string, string>; skipped: number } | null {
  const sec = sections.find((s) => s.id === secId);
  if (!sec || sec.widgetId) return null;
  const newId = freeSectionId(sec.id, sections);
  const allCardIds = new Set(sections.flatMap((s) => s.cards.map((c) => c.id)));
  const cardIds = new Map<string, string>();
  for (const c of sec.cards) { const fresh = freeCardId(c.id, allCardIds); allCardIds.add(fresh); cardIds.set(c.id, fresh); }

  const block = fields.filter((f) => f.section === secId);
  const { copies, keys, skipped } = copyBlock(block, fields, widgets, cardIds, () => newId);
  const outFields = fields.slice();
  const at = block.length ? Math.max(...block.map((f) => fields.indexOf(f))) + 1 : fields.length;
  outFields.splice(at, 0, ...copies);

  const copy: BSection = {
    ...sec,
    id: newId,
    style: { ...sec.style },
    cards: sec.cards.map((c) => ({ ...c, id: cardIds.get(c.id)!, style: { ...c.style } })),
  };
  const si = sections.indexOf(sec);
  const outSections = sections.slice();
  outSections.splice(si + 1, 0, copy);
  return { sections: outSections, fields: outFields, id: newId, keys, skipped };
}

/**
 * A pasted list, one option per line. `value | label` names both; a bare
 * line is its own value, exactly as the “Other” write-in stores what was
 * typed. Blank lines are dropped and a value repeated in the paste keeps its
 * first appearance.
 */
export function parseOptionList(text: string): FieldOption[] {
  const seen = new Set<string>();
  const out: FieldOption[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const bar = line.indexOf('|');
    const value = (bar >= 0 ? line.slice(0, bar) : line).trim();
    const label = (bar >= 0 ? line.slice(bar + 1) : line).trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push({ value, text: label || value });
  }
  return out;
}

/**
 * Pasted options joined to the ones already there. Appending keeps every
 * existing option and skips a pasted value it already has; replacing keeps
 * only the paste. An empty row (nothing typed in either box) is not an
 * option anyone meant to keep, so it goes either way.
 */
export function mergeOptions(existing: FieldOption[], pasted: FieldOption[], replace: boolean): FieldOption[] {
  if (replace) return pasted;
  const kept = existing.filter((o) => (o.value || '').trim() || (o.text || '').trim());
  const have = new Set(kept.map((o) => o.value));
  return [...kept, ...pasted.filter((o) => !have.has(o.value))];
}

export type MoveDir = 'up' | 'down';

export interface FieldMove {
  fields: BField[];
  /** Where it landed, for the announcement. */
  landed:
    | { kind: 'beside'; pos: 'above' | 'below'; anchor: BField }
    | { kind: 'into'; cont: Cont; card: BCard | null };
}

/**
 * Nudge a field one row up or down inside its container.
 *
 * A row is the unit: a field in a two-up row steps out of it and lands as a
 * row of its own above or below the neighbouring row, and cards and
 * containers count as rows too (the field moves past them, not into them).
 * Null when it is already at that end.
 */
export function moveFieldWithin(
  fields: BField[], key: string, dir: MoveDir, widgetSecIds: Set<string>
): FieldMove | null {
  const f = fields.find((x) => x.key === key);
  if (!f || isStructural(f)) return null;
  const c = contOf(f, widgetSecIds);
  const items = inCont(fields, c, widgetSecIds);
  const runs = pack(items);
  const ri = runs.findIndex((run) => run.includes(f));
  const ti = dir === 'up' ? ri - 1 : ri + 1;
  if (ri < 0 || ti < 0 || ti >= runs.length) return null;
  const target = runs[ti];
  const anchor = dir === 'up' ? target[0] : target[target.length - 1];
  const out = fields.filter((x) => x !== f);
  const moved: BField = { ...f, row: null };
  const ai = out.indexOf(anchor);
  out.splice(dir === 'up' ? ai : ai + 1, 0, moved);
  return { fields: out, landed: { kind: 'beside', pos: dir === 'up' ? 'above' : 'below', anchor } };
}

/**
 * The containers a section shows, in the order they appear: the section body
 * first, then each card as its marker comes up. Only containers in the same
 * scope as `c` — a field does not leave its repeating group this way.
 */
export function containersOfSection(
  fields: BField[], sections: BSection[], c: Cont, widgetSecIds: Set<string>
): { cont: Cont; card: BCard | null }[] {
  const sec = sections.find((s) => s.id === c.sec);
  const out: { cont: Cont; card: BCard | null }[] = [{ cont: cont(c.sec, c.scope, ''), card: null }];
  for (const f of fields) {
    if (!f.cardId || f.section !== c.sec) continue;
    const mc = contOf(f, widgetSecIds);
    if (mc.scope !== c.scope) continue;
    const card = sec?.cards.find((x) => x.id === f.cardId) || null;
    out.push({ cont: cont(c.sec, c.scope, f.cardId), card });
  }
  return out;
}

/**
 * Move a field to the neighbouring container in its section: up lands at the
 * end of the previous one, down at the start of the next, so a field walked
 * across the section passes through every position in order. Null at the ends.
 */
export function moveFieldAcross(
  fields: BField[], sections: BSection[], key: string, dir: MoveDir, widgetSecIds: Set<string>
): FieldMove | null {
  const f = fields.find((x) => x.key === key);
  if (!f || isStructural(f)) return null;
  const c = contOf(f, widgetSecIds);
  const conts = containersOfSection(fields, sections, c, widgetSecIds);
  const ci = conts.findIndex((x) => sameCont(x.cont, c));
  const ti = dir === 'up' ? ci - 1 : ci + 1;
  if (ci < 0 || ti < 0 || ti >= conts.length) return null;
  const target = conts[ti];
  const out = fields.filter((x) => x !== f);
  const moved: BField = { ...f, row: null };
  assignCont(moved, target.cont);
  const items = inCont(out, target.cont, widgetSecIds);
  let at: number;
  if (items.length) {
    at = dir === 'up' ? out.indexOf(items[items.length - 1]) + 1 : out.indexOf(items[0]);
  } else {
    // An empty card: right after its marker keeps the canvas reading in order.
    const marker = target.cont.grp ? markerOf(out, target.cont.sec, target.cont.grp) : undefined;
    at = marker ? out.indexOf(marker) + 1 : out.length;
  }
  out.splice(at, 0, moved);
  return { fields: out, landed: { kind: 'into', cont: target.cont, card: target.card } };
}
